import { OrderStatusDefinitionIdentifier } from '@order-mage/integration-sdk';

export type OrderLandingUiState = 'pending' | 'success' | 'error';

/**
 * The landing page's status entry, shared with the cart: an order handed to a payment
 * terminal seeds it before navigating, so the page has a status on its first frame.
 */
export const getOrderLandingStatusQueryKey = (orderId: string) => ['order-landing-status', orderId] as const;

type ResolveInput = {
    waitingForFirstFetch?: boolean;
    pollTimedOut?: boolean;
    statusIdentifier?: string | null;
    isOrderAvailable: boolean;
    paymentQuery?: string | null;
    /**
     * Soft-navigate after cart create (gateway hard-nav resets the form flag).
     * Skips the payment-pending flash before status loads.
     */
    justCreatedInSession?: boolean;
};

function mapStatusToUiState(statusIdentifier: string): OrderLandingUiState {
    switch (statusIdentifier) {
        case OrderStatusDefinitionIdentifier.AWAITING_PAYMENT:
            return 'pending';
        case OrderStatusDefinitionIdentifier.CANCELLED:
        case OrderStatusDefinitionIdentifier.NOT_DELIVERED:
            return 'error';
        default:
            return 'success';
    }
}

/**
 * Maps order status (preferred) or payment query to the landing UI state.
 * `payment=paid` alone never yields success — would allow a spoofed ThankYou without an order.
 */
export function resolveOrderLandingState({
    waitingForFirstFetch = false,
    pollTimedOut = false,
    statusIdentifier,
    isOrderAvailable,
    paymentQuery,
    justCreatedInSession = false,
}: ResolveInput): OrderLandingUiState {
    if (justCreatedInSession && isOrderAvailable) {
        return statusIdentifier ? mapStatusToUiState(statusIdentifier) : 'success';
    }

    if (waitingForFirstFetch) {
        return 'pending';
    }

    if (pollTimedOut && statusIdentifier === OrderStatusDefinitionIdentifier.AWAITING_PAYMENT) {
        return 'error';
    }

    if (isOrderAvailable && statusIdentifier) {
        return mapStatusToUiState(statusIdentifier);
    }

    return paymentQuery?.toLowerCase() === 'pending' ? 'pending' : 'error';
}
